import { useMsal } from '@azure/msal-react'
import { Badge, Button, Group, Stack, Table, Text, Title } from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { CommunityGem } from '../../../models/CommunityGem'
import { GemTag } from '../../../models/GemTag'
import { adminDeleteGem, adminGetAllGems, getAllTags } from '../../../data/communityGems.service'
import GemForm from './GemForm'

function GemzTable() {
  const { instance } = useMsal()
  const [gems, setGems] = useState<CommunityGem[]>([])
  const [tags, setTags] = useState<GemTag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<CommunityGem | null>(null)

  useEffect(() => {
    Promise.all([adminGetAllGems(instance), getAllTags()])
      .then(([gemsData, tagsData]) => { setGems(gemsData); setTags(tagsData) })
      .catch(() => setError('Failed to load gems. Please refresh and try again.'))
      .finally(() => setLoading(false))
  }, [instance])

  const handleSaved = (saved: CommunityGem) => {
    setGems(prev => {
      const exists = prev.find(g => g.id === saved.id)
      return exists ? prev.map(g => (g.id === saved.id ? saved : g)) : [...prev, saved]
    })
  }

  const handleDelete = (id: number) => {
    modals.openConfirmModal({
      title: 'Delete Gem',
      children: <Text size='sm'>Are you sure you want to delete this Gem? This cannot be undone.</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await adminDeleteGem(instance, id)
          setGems(prev => prev.filter(g => g.id !== id))
        } catch {
          notifications.show({
            title: 'Error',
            message: 'Failed to delete Gem. Please try again.',
            color: 'red',
          })
        }
      },
    })
  }

  if (loading) return <Text>Loading Gemz...</Text>
  if (error) return <Text c='red'>{error}</Text>

  return (
    <Stack>
      <Group justify='space-between' mt='md'>
        <Title order={4}>All Community Gemz</Title>
        <Button onClick={() => { setEditing(null); setFormOpen(true) }}>+ Add Gem</Button>
      </Group>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>City</Table.Th>
            <Table.Th>Tags</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Customer Link</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {gems.map(gem => (
            <Table.Tr key={gem.id}>
              <Table.Td>{gem.name}</Table.Td>
              <Table.Td>{[gem.city, gem.state].filter(Boolean).join(', ') || '—'}</Table.Td>
              <Table.Td>
                <Group gap='xs'>
                  {gem.tags.map(t => <Badge key={t.id} color='green' variant='light'>{t.name}</Badge>)}
                </Group>
              </Table.Td>
              <Table.Td>
                <Text c={gem.isPublished ? 'green' : 'dimmed'}>{gem.isPublished ? 'Published' : 'Draft'}</Text>
              </Table.Td>
              <Table.Td>{gem.locationId ? <Text c='green'>✓ Linked</Text> : <Text c='dimmed'>—</Text>}</Table.Td>
              <Table.Td>
                <Group gap='xs'>
                  <Button size='xs' variant='subtle' onClick={() => { setEditing(gem); setFormOpen(true) }}>Edit</Button>
                  <Button size='xs' variant='subtle' color='red' onClick={() => handleDelete(gem.id)}>Delete</Button>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <GemForm gem={editing} tags={tags} opened={formOpen} onClose={() => setFormOpen(false)} onSaved={handleSaved} />
    </Stack>
  )
}

export default GemzTable
